import { useState, useEffect } from "react"
import { Modal } from "@/components/common/Modal"
import { Badge, statusBadge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { Spinner } from "@/components/common/Spinner"
import { EmptyState } from "@/components/common/EmptyState"
import type { UserWithSubscriptions } from "@/services/user.service"
import { getUserNotes, sendPasswordReset, suspendUser, unsuspendUser, takeoverSlot } from "@/services/user.service"
import { useToast } from "@/context/ToastContext"
import { supabase } from "@/lib/supabase"
import type { AdminNote } from "@/types"
import { clsx } from "clsx"
import {
  User, CreditCard, Network, DollarSign,
  StickyNote, Zap, KeyRound, Ban,
  CheckCircle, Building2
} from "lucide-react"

const TABS = [
  { id: "info",          label: "Info",          icon: <User size={14} /> },
  { id: "subscriptions", label: "Subscriptions", icon: <CreditCard size={14} /> },
  { id: "network",       label: "Network",       icon: <Network size={14} /> },
  { id: "commissions",   label: "Commissions",   icon: <DollarSign size={14} /> },
  { id: "notes",         label: "Notes",         icon: <StickyNote size={14} /> },
  { id: "actions",       label: "Actions",       icon: <Zap size={14} /> },
]

// ── Tab: Info ──────────────────────────────────────────────────

function InfoTab({ user }: { user: UserWithSubscriptions }) {
  const rows = [
    { label: "Full Name",     value: user.full_name ?? "—" },
    { label: "Email",         value: user.email },
    { label: "Status",        value: statusBadge(user.subscription_status, user.is_suspended) },
    { label: "User ID",       value: <span className="font-mono text-xs text-gray-400">{user.id}</span> },
    { label: "Referral Code", value: <span className="font-mono font-bold text-zimrah-600">{user.referral_code ?? "—"}</span> },
    { label: "Network Size",  value: <span className="font-black text-gray-900">{user.network_size ?? 0}</span> },
    { label: "Total Earnings",value: <span className="font-black text-green-600">${(user.total_earnings ?? 0).toFixed(2)}</span> },
    { label: "Member Since",  value: new Date(user.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) },
  ]

  return (
    <div className="space-y-1">
      {rows.map(r => (
        <div key={r.label} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
          <span className="text-sm font-semibold text-gray-500 w-36 shrink-0">{r.label}</span>
          <span className="text-sm text-gray-900 text-right flex-1">{r.value}</span>
        </div>
      ))}
      {user.is_suspended && (
        <div className="mt-3 p-4 rounded-xl bg-red-50 border border-red-100">
          <p className="text-xs font-bold text-red-600 mb-1">Suspension Details</p>
          <p className="text-xs text-red-500">Reason: {user.suspension_reason ?? "Not specified"}</p>
          {user.suspended_at && (
            <p className="text-xs text-red-400 mt-0.5">
              Suspended: {new Date(user.suspended_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Tab: Subscriptions ─────────────────────────────────────────

function SubscriptionsTab({ user }: { user: UserWithSubscriptions }) {
  const subs = user.subscriptions ?? []
  const emojiMap: Record<string, string> = { zimrah: "🌸", hpc: "📅", almanac: "🌾" }

  if (subs.length === 0) return <EmptyState icon="📭" title="No subscriptions" subtitle="This user has no active product subscriptions." />

  return (
    <div className="space-y-3">
      {subs.map(s => (
        <div key={s.id} className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 bg-gray-50">
          <span className="text-2xl">{emojiMap[s.product_slug] ?? "📱"}</span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 capitalize">{s.product_slug}</p>
            {s.grace_period_end && (
              <p className="text-xs text-amber-600 mt-0.5">
                Grace ends: {new Date(s.grace_period_end).toLocaleDateString()}
              </p>
            )}
          </div>
          {s.status === "active"
            ? <Badge variant="success" dot>Active</Badge>
            : s.status === "grace_period"
            ? <Badge variant="warning" dot>Grace Period</Badge>
            : <Badge variant="muted" dot>Inactive</Badge>
          }
        </div>
      ))}
    </div>
  )
}

// ── Tab: Commissions ───────────────────────────────────────────

function CommissionsTab({ user }: { user: UserWithSubscriptions }) {
  const [data, setData]     = useState<Record<string, unknown>[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase
      .from("commission_transactions")
      .select("*")
      .eq("member_id", user.id)
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => { setData(data ?? []); setLoading(false) })
  }, [user.id])

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>
  if (data.length === 0) return <EmptyState icon="💰" title="No commissions yet" subtitle="Commissions will appear here as referrals join." />

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            {["Date", "Type", "Amount", "Status"].map(h => (
              <th key={h} className="px-4 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((c: Record<string, unknown>) => (
            <tr key={c.id as string} className="border-t border-gray-50 hover:bg-gray-50">
              <td className="px-4 py-3 text-gray-500 text-xs">{new Date(c.created_at as string).toLocaleDateString()}</td>
              <td className="px-4 py-3 capitalize text-gray-700">{(c.type as string)?.replace(/_/g, " ")}</td>
              <td className="px-4 py-3 font-bold text-gray-900">${(c.amount as number)?.toFixed(2)}</td>
              <td className="px-4 py-3">
                {c.status === "paid"
                  ? <Badge variant="success">{c.status as string}</Badge>
                  : c.status === "pending"
                  ? <Badge variant="warning">{c.status as string}</Badge>
                  : <Badge variant="muted">{c.status as string}</Badge>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Tab: Notes ─────────────────────────────────────────────────

function NotesTab({ user }: { user: UserWithSubscriptions }) {
  const [notes, setNotes]   = useState<AdminNote[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getUserNotes(user.id).then(n => { setNotes(n); setLoading(false) })
  }, [user.id])

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>
  if (notes.length === 0) return <EmptyState icon="📝" title="No notes yet" subtitle="Admin notes for this user will appear here." />

  const colors: Record<string, string> = {
    general: "bg-gray-50 border-gray-200",
    support: "bg-blue-50 border-blue-200",
    warning: "bg-amber-50 border-amber-200",
    issue:   "bg-red-50 border-red-200",
    resolution: "bg-green-50 border-green-200",
  }

  return (
    <div className="space-y-3">
      {notes.map(n => (
        <div key={n.id} className={`p-4 rounded-xl border ${colors[n.note_type] ?? colors.general}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 capitalize">{n.note_type}</p>
            <p className="text-xs text-gray-400">{new Date(n.created_at).toLocaleDateString()}</p>
          </div>
          {n.title && <p className="font-bold text-sm text-gray-900 mb-1">{n.title}</p>}
          <p className="text-sm text-gray-700 leading-relaxed">{n.content}</p>
        </div>
      ))}
    </div>
  )
}

// ── Tab: Network ───────────────────────────────────────────────

function NetworkTab({ user }: { user: UserWithSubscriptions }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl bg-zimrah-50 border border-zimrah-100 text-center">
          <p className="text-3xl font-black text-zimrah-700">{user.network_size ?? 0}</p>
          <p className="text-xs font-bold text-zimrah-500 uppercase tracking-wider mt-1">Total Downline</p>
        </div>
        <div className="p-5 rounded-2xl bg-green-50 border border-green-100 text-center">
          <p className="text-3xl font-black text-green-700">${(user.total_earnings ?? 0).toFixed(2)}</p>
          <p className="text-xs font-bold text-green-500 uppercase tracking-wider mt-1">Total Earned</p>
        </div>
      </div>
      {user.referral_code && (
        <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Referral Code</p>
          <p className="text-lg font-black text-zimrah-600 font-mono">{user.referral_code}</p>
        </div>
      )}
      <p className="text-xs text-gray-400 text-center">Detailed network tree — Phase 4</p>
    </div>
  )
}

// ── Tab: Actions ───────────────────────────────────────────────

function ActionsTab({ user, onRefresh, onClose }: {
  user: UserWithSubscriptions
  onRefresh: () => void
  onClose: () => void
}) {
  const { toast } = useToast()
  const [loading, setLoading] = useState<string | null>(null)

  const run = async (key: string, fn: () => Promise<void>) => {
    setLoading(key)
    try { await fn(); onRefresh(); onClose() }
    catch (err: unknown) { toast((err instanceof Error ? err.message : "Action failed"), "error") }
    finally { setLoading(null) }
  }

  const actions = [
    {
      key: "email",
      label: "Send Password Reset",
      icon: <KeyRound size={16} />,
      color: "hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700",
      fn: () => sendPasswordReset(user.id, user.email),
    },
    {
      key: "suspend",
      label: user.is_suspended ? "Unsuspend User" : "Suspend User",
      icon: user.is_suspended ? <CheckCircle size={16} /> : <Ban size={16} />,
      color: user.is_suspended
        ? "hover:bg-green-50 hover:border-green-200 hover:text-green-700"
        : "hover:bg-red-50 hover:border-red-200 hover:text-red-700",
      fn: () => user.is_suspended ? unsuspendUser(user.id) : suspendUser(user.id, "Admin action"),
    },
    {
      key: "takeover",
      label: "Company Takeover",
      icon: <Building2 size={16} />,
      color: "hover:bg-red-50 hover:border-red-200 hover:text-red-700",
      fn: () => takeoverSlot(user.id, "admin_action"),
    },
  ]

  return (
    <div className="space-y-2">
      {actions.map(a => (
        <button
          key={a.key}
          onClick={() => run(a.key, a.fn)}
          disabled={!!loading}
          className={clsx(
            "w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed",
            a.color
          )}
        >
          {loading === a.key ? <Spinner size="sm" /> : a.icon}
          {a.label}
        </button>
      ))}
      <div className="pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-400 text-center">More actions (adjust commission, transfer) — Phase 4</p>
      </div>
    </div>
  )
}

// ── Main Modal ─────────────────────────────────────────────────

interface Props {
  user: UserWithSubscriptions
  open: boolean
  onClose: () => void
  onRefresh: () => void
}

export function UserDetailModal({ user, open, onClose, onRefresh }: Props) {
  const [tab, setTab] = useState("info")

  useEffect(() => { if (open) setTab("info") }, [open])

  const TAB_CONTENT: Record<string, React.ReactNode> = {
    info:          <InfoTab user={user} />,
    subscriptions: <SubscriptionsTab user={user} />,
    network:       <NetworkTab user={user} />,
    commissions:   <CommissionsTab user={user} />,
    notes:         <NotesTab user={user} />,
    actions:       <ActionsTab user={user} onRefresh={onRefresh} onClose={onClose} />,
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={user.full_name ?? user.email}
      icon="👤"
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>Close</Button>
      }
    >
      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-50 rounded-xl p-1 -mx-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-bold transition-all",
              tab === t.id
                ? "bg-white text-zimrah-700 shadow-sm"
                : "text-gray-400 hover:text-gray-600"
            )}
          >
            {t.icon}
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>
      {/* Tab content */}
      <div className="min-h-[300px]">
        {TAB_CONTENT[tab]}
      </div>
    </Modal>
  )
}